import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import favorite from '@gin-betting/favorite';
import core, { GQL } from '@gin-betting/core';
import { partition } from 'lodash';

import ui from 'modules/ui';
import { ACTIVE_MARKET_STATUSES, isMobile } from 'app-constants';
import { getDefaultVariablesFavorite } from 'utils';
import GET_SPORT_EVENT_IDS_BY_FILTERS, {
    GetSportEventIdsByFiltersQuery,
} from './gqlGetSportEventIdsByFilters';

const {
    appServiceLocator,
    utils: { getBettingOptions },
} = core;
const {
    actions: { changeFavoriteCount },
} = ui;

const {
    selectors: { selectFavoriteIds },
} = favorite;

const defaultVariables = {
    matchStatuses: [
        GQL.SportEventStatus.NOT_STARTED,
        GQL.SportEventStatus.SUSPENDED,
        GQL.SportEventStatus.LIVE,
    ],
    marketStatuses: ACTIVE_MARKET_STATUSES,
    sportEventTypes: [GQL.SportEventType.MATCH, GQL.SportEventType.OUTRIGHT],
};

function useFavoriteCountAll(): void {
    const dispatch = useDispatch();
    const favoriteIds = useSelector(selectFavoriteIds);
    const isRequest = useRef<boolean>(false);

    const getFavoriteCountFromSportEvents = useCallback(() => {
        const { sportIds } = getDefaultVariablesFavorite();

        const variables: GQL.GetMatchesByEventIdsQueryVariables = {
            ...defaultVariables,
            sportEventIds: favoriteIds.toArray(),
            // TODO: Refactor Favorite Count: fix counter, avoid obsolete requests on mobile
            sportIds: isMobile
                ? getBettingOptions().selectedSportIds
                : sportIds,
        };

        appServiceLocator
            .get('apiClient')
            .makeRequestWithCustomQuery({
                query: GET_SPORT_EVENT_IDS_BY_FILTERS,
                variables,
                action: {
                    type: 'favorite/GET_SPORT_EVENT_IDS_BY_FILTERS',
                    payload: {},
                },
            })
            .then(({ response }) => {
                const {
                    matches,
                } = response.data as GetSportEventIdsByFiltersQuery;

                const [upcoming, outrights] = partition(
                    matches,
                    match => match.fixture.type === GQL.SportEventType.MATCH
                );

                dispatch(
                    changeFavoriteCount({
                        type: 'upcoming',
                        count: upcoming.length,
                    })
                );

                dispatch(
                    changeFavoriteCount({
                        type: 'outrights',
                        count: outrights.length,
                    })
                );
            })
            .catch(err => {
                console.error(err);
            });
    }, [favoriteIds, dispatch]);

    useEffect(() => {
        if (favoriteIds.size === 0 || isRequest.current) return;

        isRequest.current = true;

        getFavoriteCountFromSportEvents();
    }, [favoriteIds, getFavoriteCountFromSportEvents]);
}

export default useFavoriteCountAll;
